import { useCallback, useMemo, useState } from 'react';

import { Table } from '@components/common';

import { useMutateRUDashboardStatusUpdateCarrier } from '@hooks/ruDashboardState/useMutateRUDashboardStatusUpdateCarrier';
import { useQueryRUDashboardStatusCarriersState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusCarriersState';

import { IId, TNameTitle } from '@interfaces/common/common';
import { ISortBy, ITableSort } from '@interfaces/components/table';
import { ICarrier } from '@interfaces/ruStatusDashboard/ruStatusDashboard';

import { ECarrierStates } from './constants';

import { getColumns } from './utils';

import styles from './CarrierStateTable.module.scss';

interface ICarrierStateTableProps extends TNameTitle, IId {
	isEnergySavingMode: boolean;
	carrierName?: string;
}

const CarrierStateTable = ({
	name,
	title,
	id,
	carrierName,
}: ICarrierStateTableProps) => {
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const {
		ruDashboardStatusCarriersState,
		isLoadingRUDashboardStatusCarriers,
	} = useQueryRUDashboardStatusCarriersState({ ruNetworkElementId: id });

	const carriers = useMemo(() => {
		return ruDashboardStatusCarriersState.length ? [{
			carriers: ruDashboardStatusCarriersState.map((carrier) => carrier.name).join(', '),
			state: ruDashboardStatusCarriersState[0]?.state || {},
			active: ruDashboardStatusCarriersState[0]?.active || {},
			name: carrierName || '',
		}] : [];
	}, [ruDashboardStatusCarriersState, carrierName]);

	const isInactivateAvailable = ruDashboardStatusCarriersState?.some((carrier: ICarrier) => {
		return carrier.active?.name === ECarrierStates.ACTIVE;
	});

	const isActivateAvailable = ruDashboardStatusCarriersState?.some((carrier: ICarrier) => {
		return (
			carrier.active?.name === ECarrierStates.SLEEP
			|| carrier.active?.name === ECarrierStates.INACTIVE
		);
	});

	const isSleepAvailable = isInactivateAvailable;

	const {
		updateRUStatusDashboardCarrierState,
		isLoadingCarrierStatusUpdate,
	} = useMutateRUDashboardStatusUpdateCarrier();

	const handleActivate = useCallback(() => {
		updateRUStatusDashboardCarrierState({
			ruNetworkElementId: id,
			state: ECarrierStates.ACTIVE,
		});
	}, [id, updateRUStatusDashboardCarrierState]);

	const handleSleep = useCallback(() => {
		updateRUStatusDashboardCarrierState({
			ruNetworkElementId: id,
			state: ECarrierStates.SLEEP,
		});
	}, [id, updateRUStatusDashboardCarrierState]);

	const handleInactivate = useCallback(() => {
		updateRUStatusDashboardCarrierState({
			ruNetworkElementId: id,
			state: ECarrierStates.INACTIVE,
		});
	}, [id, updateRUStatusDashboardCarrierState]);

	const columns = useMemo(() => getColumns({
		handleSleep,
		handleInactivate,
		handleActivate,
		isSleepAvailable,
		isInactivateAvailable,
		isActivateAvailable,
	}), [
		handleSleep,
		handleActivate,
		handleInactivate,
		isSleepAvailable,
		isInactivateAvailable,
		isActivateAvailable,
	]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const isLoading = isLoadingRUDashboardStatusCarriers || isLoadingCarrierStatusUpdate;

	return (
		<div className={styles.wrapper}>
			<div className={styles.headerWrapper}>
				<div className={styles.header}>{title}</div>
				<div className={styles.name}>{name}</div>
			</div>
			<div className={styles.container}>
				<Table
					columns={columns}
					data={carriers}
					isDeleteActionDisabled
					isLoading={isLoading}
					isWithPagination={false}
					isWithoutActions
					isWithoutCheckbox
					isWithoutDeleteAction
					isWithoutFilter
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default CarrierStateTable;
